/**
 * Project Redux slice
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { projectAPI } from '@/api/projectAPI';
import { Project, ProjectWithDetails, ProjectSummary, CreateProjectData, UpdateProjectData } from '@/types';
import toast from 'react-hot-toast';

interface ProjectState {
  projects: Project[];
  currentProject: ProjectWithDetails | null;
  currentProjectSummary: ProjectSummary | null;
  loading: boolean;
  error: string | null;
  // Last successful fetch params + timestamp, used to dedupe simultaneous
  // dispatches from different mount points (e.g. Sidebar + Tasks page).
  lastFetchedKey?: string;
  lastFetchedAt?: number;
}

const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  currentProjectSummary: null,
  loading: false,
  error: null,
};

const PROJECTS_TTL_MS = 30_000;
const projectsKey = (params: any) =>
  JSON.stringify({
    o: params?.organization_id || '',
    t: params?.team_id || '',
    a: params?.is_active ?? null,
  });

// Async thunks
export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async (params: {
    organization_id?: string;
    team_id?: string;
    owner_id?: string;
    is_active?: boolean;
    is_public?: boolean;
    skip?: number;
    limit?: number;
    order_by?: string;
    order_direction?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const projects = await projectAPI.getProjects(params || {});
      return projects;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch projects');
    }
  },
  {
    // Skip the dispatch when a fetch with the same params is already running
    // or completed within the TTL. Mutations (create/update/delete) clear the
    // timestamp so subsequent fetches go through.
    condition: (params, { getState }) => {
      const state = (getState() as any).projects as ProjectState;
      if (state?.loading) return false;
      const key = projectsKey(params);
      const ts = state?.lastFetchedAt || 0;
      if (state?.lastFetchedKey === key && Date.now() - ts < PROJECTS_TTL_MS) {
        return false;
      }
      return true;
    },
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchOne',
  async (id: string, { rejectWithValue }) => {
    try {
      const project = await projectAPI.getProject(id);
      return project;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch project');
    }
  }
);

export const fetchProjectSummary = createAsyncThunk(
  'projects/fetchSummary',
  async (id: string, { rejectWithValue }) => {
    try {
      const summary = await projectAPI.getProjectSummary(id);
      return summary;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch project summary');
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/create',
  async (data: CreateProjectData, { rejectWithValue }) => {
    try {
      const project = await projectAPI.createProject(data);
      toast.success('Project created successfully!');
      return project;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
      return rejectWithValue(error.response?.data?.detail || 'Failed to create project');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/update',
  async ({ id, data }: { id: string; data: UpdateProjectData }, { rejectWithValue }) => {
    try {
      const project = await projectAPI.updateProject(id, data);
      toast.success('Project updated successfully!');
      return project;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
      return rejectWithValue(error.response?.data?.detail || 'Failed to update project');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await projectAPI.deleteProject(id);
      toast.success('Project deleted successfully!');
      return id;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete project');
    }
  }
);

export const archiveProject = createAsyncThunk(
  'projects/archive',
  async (id: string, { rejectWithValue }) => {
    try {
      const project = await projectAPI.archiveProject(id);
      toast.success('Project archived successfully!');
      return project;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to archive project');
      return rejectWithValue(error.response?.data?.detail || 'Failed to archive project');
    }
  }
);

export const activateProject = createAsyncThunk(
  'projects/activate',
  async (id: string, { rejectWithValue }) => {
    try {
      const project = await projectAPI.activateProject(id);
      toast.success('Project activated successfully!');
      return project;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to activate project');
      return rejectWithValue(error.response?.data?.detail || 'Failed to activate project');
    }
  }
);

// Project slice
const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
    },
    clearCurrentProject: (state) => {
      state.currentProject = null;
    },
    clearCurrentProjectSummary: (state) => {
      state.currentProjectSummary = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
        state.lastFetchedKey = projectsKey(action.meta.arg);
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch single project
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = action.payload;
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch project summary
      .addCase(fetchProjectSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProjectSummary = action.payload;
      })
      .addCase(fetchProjectSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.unshift(action.payload);
        state.lastFetchedAt = 0;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update project
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject && state.currentProject.id === action.payload.id) {
          state.currentProject = { ...state.currentProject, ...action.payload };
        }
        state.lastFetchedAt = 0;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete project
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(project => project.id !== action.payload);
        if (state.currentProject && state.currentProject.id === action.payload) {
          state.currentProject = null;
        }
        state.lastFetchedAt = 0;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Archive project
      .addCase(archiveProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject && state.currentProject.id === action.payload.id) {
          state.currentProject = { ...state.currentProject, ...action.payload };
        }
      })
      .addCase(archiveProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Activate project
      .addCase(activateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject && state.currentProject.id === action.payload.id) {
          state.currentProject = { ...state.currentProject, ...action.payload };
        }
      })
      .addCase(activateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentProject, clearCurrentProject, clearCurrentProjectSummary, clearError } = projectSlice.actions;
export default projectSlice.reducer;